import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import DefaultModal from '../../../../../components/DefaultModal'
import FormButton from '../../../../../components/FormButton'
import { RootState } from '../../../../../redux/store'

interface PortfolioManagementAgreementModalProps {
  isOpen: boolean
  onClose: () => void
  onAccept: () => void
  isLoading: boolean
}

// Section: title + horizontal rule below, then content
const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="mb-8">
    <h2 className="text-base font-semibold mb-3">{title}</h2>
    <div className="space-y-0">{children}</div>
    <hr className="mt-6 border-black/10 dark:border-white/10" />
  </div>
)

// Sub: number column + bold title column on same row, body indented under title
const Sub: React.FC<{
  num: string
  title: string
  children: React.ReactNode
}> = ({ num, title, children }) => (
  <div className="mb-5">
    <div className="flex gap-6 mb-1">
      <span className="font-medium w-10 shrink-0 text-right">{num}</span>
      <span className="font-medium">{title}</span>
    </div>
    <div className="pl-[calc(2.5rem+1.5rem)] leading-relaxed text-description">
      {children}
    </div>
  </div>
)

const PortfolioManagementAgreementModal: React.FC<
  PortfolioManagementAgreementModalProps
> = ({ isOpen, onClose, onAccept, isLoading }) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const user = useSelector((state: RootState) => state.session.user)

  const handleScroll = () => {
    if (!scrollContainerRef.current) return
    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current
    if (scrollTop + clientHeight >= scrollHeight - 20 && !hasScrolledToBottom) {
      setHasScrolledToBottom(true)
    }
  }

  useEffect(() => {
    if (!isOpen) setHasScrolledToBottom(false)
  }, [isOpen])

  return (
    <DefaultModal
      isOpen={isOpen}
      onClose={onClose}
      isLoading={isLoading}
      inlineLoading
      title="Portfolio Management Agreement"
      maxW="900px"
    >
      <div
        ref={scrollContainerRef}
        className="p-8 max-h-[70vh] overflow-y-auto"
        onScroll={handleScroll}
      >
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-xl font-semibold tracking-wide mb-4">
            PORTFOLIO MANAGEMENT AGREEMENT
          </h1>
          <div className="text-sm text-description space-y-0.5 mb-3">
            <p>Penning Financial Services ApS</p>
            <p>Version: v1 &nbsp;·&nbsp; Effective Date: March 13, 2026</p>
            <p>
              Governing Law: Denmark &nbsp;·&nbsp; Language: English (prevails
              in case of conflict)
            </p>
          </div>
          <p className="text-sm mt-3">
            Effective between <strong>Penning Financial Services ApS</strong>{' '}
            and <strong>{user.name}</strong>
          </p>
          <hr className="mt-6 border-black/10 dark:border-white/10" />
        </div>

        <div className="text-sm">
          <Section title="1. Acceptance & Legal Effect">
            <Sub num="1.1" title="Acceptance by Click-Through">
              By completing onboarding on our website and clicking the checkbox
              "I have read, understood, and accept the Portfolio Management
              Agreement", you adopt an electronic signature and enter a legally
              binding agreement with Penning. A timestamped acceptance receipt
              will be stored in your account.
            </Sub>
            <Sub num="1.2" title="Client">
              "You", "the Client" refers to the individual or legal entity who
              completes onboarding.
            </Sub>
            <Sub num="1.3" title="Eligibility">
              You confirm you are legally capable of entering a contract, not a
              U.S. Person, and not subject to sanctions or jurisdictional
              restrictions that prohibit your use of crypto-related financial
              services.
            </Sub>
          </Section>

          <Section title="2. Definitions">
            <ul className="list-disc pl-6 space-y-2 leading-relaxed text-description">
              <li>
                <span className="font-medium">Package:</span> Any discretionary
                portfolio management option offered by Penning, present or
                future.
              </li>
              <li>
                <span className="font-medium">Package Specifications:</span> The
                commercial and risk details of a specific Package presented to
                you at enrollment (allocation universe, minimum investment,
                fees, lock-in, redemption terms, custody model, risk label,
                liquidity, and other parameters).
              </li>
              <li>
                <span className="font-medium">Digital Assets:</span>{' '}
                Cryptocurrencies, tokens, stablecoins, wrapped assets, synthetic
                assets, staking assets, and related derivatives.
              </li>
              <li>
                <span className="font-medium">Custodian / Prime Broker:</span>{' '}
                Exchanges, custodians, prime brokers, OTC venues, liquidity
                providers, staking providers, or derivatives venues used by
                Penning.
              </li>
            </ul>
          </Section>

          <Section title="3. Nature of Services">
            <Sub num="3.1" title="Discretionary Mandate">
              By selecting and enrolling in a Package, you authorize Penning to
              manage your portfolio at our discretion, within the boundaries of
              that Package. This may include spot assets, derivatives, leverage,
              algorithmic strategies, staking, yield protocols,
              borrowing/lending, stablecoins, and rebalancing.
            </Sub>
            <Sub num="3.2" title="Execution">
              We may aggregate orders, execute across multiple venues, and
              allocate fills in a fair and systematic manner.
            </Sub>
            <Sub num="3.3" title="No Advisory Role">
              Penning does not provide investment advice, financial advice,
              legal advice, or tax advice. Package descriptions, past returns,
              and model/backtest results are not guarantees.
            </Sub>
            <Sub num="3.4" title="Reinvestment of Gains">
              Unless specified otherwise in the Package Specifications, realized
              returns are automatically reinvested.
            </Sub>
          </Section>

          <Section title="4. Onboarding, KYC, AML & Compliance">
            <Sub num="4.1" title="Verification Requirements">
              You must complete all KYC/KYB, AML, sanctions, and PEP screenings.
              We may require re-verification at any time, and may suspend or
              terminate services if requirements are not met.
            </Sub>
            <Sub num="4.2" title="Source of Funds">
              You confirm that all funds originate from lawful sources and are
              not encumbered by liens or restrictions.
            </Sub>
          </Section>

          <Section title="5. Packages & Package Specifications">
            <Sub num="5.1" title="Package Menu">
              Penning may add, modify, pause, or retire Packages at any time.
              Current examples include cycle-aware long-term crypto strategies
              and multi-strategy algorithmic trading strategies, but future
              Packages may differ entirely.
            </Sub>
            <Sub num="5.2" title="Binding Incorporation">
              The Package Specifications displayed to you at the moment of
              enrollment are incorporated into this Agreement.
            </Sub>
            <Sub num="5.3" title="Future-Proof Design">
              New Packages may introduce new asset types, protocols, chains,
              forms of leverage, or liquidity structures. Enrollment in any new
              Package requires fresh click-to-accept approval.
            </Sub>
            <Sub num="5.4" title="Changes to Packages">
              Material changes (e.g., fee structure or lock-in changes) will be
              communicated and may require renewed acceptance. Non-material
              updates may be applied without additional consent.
            </Sub>
          </Section>

          <Section title="6. Funding, Custody, and Wallet Operations">
            <Sub num="6.1" title="Funding">
              Accepted currencies and funding channels (e.g., EUR bank transfer,
              stablecoins, BTC/ETH) are stated in the Package Specifications.
              Network fees and banking costs are borne by you.
            </Sub>
            <Sub num="6.2" title="Custody Structure">
              Assets may be held on exchanges, in omnibus or segregated
              accounts, or in institutional hot/warm/cold/MPC wallets.
              Venue-level collateral may be required for derivatives. Unless
              stated otherwise, Penning does not rehypothecate your spot assets.
            </Sub>
            <Sub num="6.3" title="Staking and On-Chain Interaction">
              If applicable to your Package, staking/DeFi interactions may incur
              protocol slashing, governance changes, smart-contract failures,
              validator risks, or slippage.
            </Sub>
            <Sub num="6.4" title="Forks, Airdrops, Bridges">
              Penning retains full discretion on if/how to support forks,
              airdrops, or cross-chain bridge interactions. Technical or venue
              limitations may prevent certain claims.
            </Sub>
          </Section>

          <Section title="7. Valuation & Reporting">
            <Sub num="7.1" title="Valuation Methodology">
              Assets are valued using good-faith methodologies: exchange prices,
              independent data sources, settlement marks, or model-based fair
              value (for derivatives).
            </Sub>
            <Sub num="7.2" title="Reporting">
              Reporting is provided in the form of monitoring via the client
              dashboard, where portfolio status and performance is tracked. The
              frequency by which the dashboard is updated may vary during
              volatile markets or operational constraints.
            </Sub>
            <Sub num="7.3" title="Error Corrections">
              Administrative or pricing errors may be corrected retrospectively
              in good faith.
            </Sub>
          </Section>

          <Section title="8. Subscriptions, Redemptions & Liquidity">
            <Sub num="8.1" title="Minimums and Add-Ons">
              Each Package defines its minimum investment and top-up rules. Many
              current Packages use a €5,000 minimum, but future Packages may
              differ.
            </Sub>
            <Sub num="8.2" title="Lock-In and Notice">
              <p className="mb-3">
                Lock-ins (e.g., 24-hour minimum), redemption notice periods, or
                gating mechanisms are specified in the Package Specifications.
                During distressed markets, Penning may impose gates, pro-rata
                redemptions, or temporary suspension.
              </p>
              <p>
                Penning retains the right to ban the Client on the basis of any
                abuse of lock-ins; e.g. if a client gives Penning a 24-hours
                notice to seize portfolio management, triggering subsequent
                redemption processes, and then the Client re-subscribing to the
                same portfolio management package shortly thereafter. What
                "abuse of lock-ins" entails remains at the sole discretion of
                Penning to determine.
              </p>
            </Sub>
            <Sub num="8.3" title="Settlement">
              Redemption payments are processed after reconciliations and venue
              settlements, subject to delays beyond Penning's control.
            </Sub>
          </Section>

          <Section title="9. Fees & Costs">
            <Sub num="9.1" title="Fee Structure">
              Package fees (e.g., performance-based fees such as 30% on realized
              profits up to €50k tier and 15% above, or 0% management fee) are
              defined in the Package Specifications.
            </Sub>
            <Sub num="9.2" title="Other Costs">
              Exchange fees, funding/borrowing costs, network fees, and custody
              fees are deducted from portfolio value.
            </Sub>
            <Sub num="9.3" title="Taxes">
              You are solely responsible for tax reporting and payments. Penning
              does not provide tax advice.
            </Sub>
          </Section>

          <Section title="10. Comprehensive Risk Disclosure">
            <p className="leading-relaxed text-description mb-5 pl-[calc(2.5rem+1.5rem)]">
              Investing in Digital Assets involves extreme risk. You may lose
              your entire investment. Risks include, without limitation:
            </p>
            <Sub num="10.1" title="Market Volatility">
              Crypto's price cycles exhibit extreme drawdowns (e.g., −10% to
              −60% ranges shown in current strategies). Backtests cannot predict
              future performance.
            </Sub>
            <Sub num="10.2" title="Liquidity & Market Structure">
              Order-book depth may vanish; stablecoins may de-peg.
            </Sub>
            <Sub num="10.3" title="Leverage & Derivatives">
              Futures/perpetuals/options may cause rapid liquidation,
              auto-deleverage, or funding losses.
            </Sub>
            <Sub num="10.4" title="Counterparty Risk">
              Exchanges, custodians, or stablecoins may fail, freeze
              withdrawals, or become insolvent.
            </Sub>
            <Sub num="10.5" title="Technology Risk">
              Smart-contract failures, bridge exploits, validator penalties, key
              compromise, MEV, chain reorganizations.
            </Sub>
            <Sub num="10.6" title="Regulatory & Legal Risk">
              Changing EU/Danish/global laws, exchange shutdowns, enforcement
              actions.
            </Sub>
            <Sub num="10.7" title="Algorithmic/Model Risk">
              Algorithmic strategies may misfire in unexpected regimes or suffer
              data/model drift.
            </Sub>
            <Sub num="10.8" title="Geopolitical & Macro Instability">
              Wars, sanctions, capital controls, energy crises, pandemics, and
              banking crises may impair valuation or redemptions.
            </Sub>
            <Sub num="10.9" title="Operational Risk">
              Human error, vendor failures, or data issues.
            </Sub>
            <Sub num="10.10" title="No Guarantees">
              No capital protection, no guaranteed APR, and no guarantee of
              profit.
            </Sub>
          </Section>

          <Section title="11. Internet & Electronic Communications">
            <p className="leading-relaxed text-description pl-[calc(2.5rem+1.5rem)]">
              Penning is not responsible for outages, delays, routing errors,
              API failures, or network instability beyond our control.
            </p>
          </Section>

          <Section title="12. Intellectual Property & Confidentiality">
            <p className="leading-relaxed text-description pl-[calc(2.5rem+1.5rem)]">
              All strategies, models, and systems are Penning's intellectual
              property. You may not reverse-engineer or disclose them.
            </p>
          </Section>

          <Section title="13. Data Protection & Privacy">
            <p className="leading-relaxed text-description pl-[calc(2.5rem+1.5rem)]">
              We process personal data under our Privacy Notice. Identity data
              is stored in accordance with AML rules and may be shared with
              regulatory authorities if required.
            </p>
          </Section>

          <Section title="14. Liability & Indemnity">
            <Sub num="14.1" title="Limitation of Liability">
              Penning is not liable for indirect, incidental, consequential, or
              punitive damages; nor for losses arising from exchange insolvency,
              cyberattacks, market halts, regulatory actions, or protocol
              failures — except in cases of Penning's willful misconduct or
              fraud.
            </Sub>
            <Sub num="14.2" title="Indemnification">
              You indemnify Penning against claims arising from your breach of
              this Agreement.
            </Sub>
          </Section>

          <Section title="15. Changes to Agreement">
            <p className="leading-relaxed text-description pl-[calc(2.5rem+1.5rem)]">
              We may update this Agreement for regulatory, operational, or
              security purposes. Material changes requiring your acceptance will
              be presented through your dashboard with click-to-accept.
            </p>
          </Section>

          <Section title="16. Termination">
            <p className="leading-relaxed text-description pl-[calc(2.5rem+1.5rem)] mb-3">
              Penning may suspend or terminate services immediately for
              regulatory, AML, or security reasons.
            </p>
            <p className="leading-relaxed text-description pl-[calc(2.5rem+1.5rem)]">
              You may terminate per the rules of your Package (e.g., respecting
              lock-ins and notice periods).
            </p>
          </Section>

          <Section title="17. Governing Law & Disputes">
            <p className="leading-relaxed text-description pl-[calc(2.5rem+1.5rem)]">
              Danish law governs this Agreement. Disputes are resolved through
              Danish courts, after attempts at amicable resolution.
            </p>
          </Section>

          <Section title="18. Acknowledgement">
            <p className="leading-relaxed text-description pl-[calc(2.5rem+1.5rem)] mb-3">
              By clicking the acceptance button, you confirm:
            </p>
            <ul className="list-disc pl-[calc(2.5rem+1.5rem+1rem)] space-y-1.5 leading-relaxed text-description">
              <li>You have read, understood, and accepted the Agreement.</li>
              <li>
                You understand the high risks associated with Digital Assets,
                derivatives, leverage, and technology.
              </li>
              <li>
                You accept that geopolitical instability may affect market
                access and valuations.
              </li>
              <li>
                You understand there are no guarantees of returns or capital
                protection.
              </li>
              <li>
                You consent to electronic communication, KYC/AML, and
                Package-specific fees.
              </li>
              <li>You take sole responsibility for your tax affairs.</li>
              <li>
                You confirm your funds are lawful and you are not a U.S. Person.
              </li>
            </ul>
          </Section>
        </div>
      </div>

      <div className="p-6 pt-4 flex justify-center border-t border-black/5 dark:border-white/10">
        <div className="max-w-[600px] w-full">
          {!hasScrolledToBottom && (
            <div className="text-amber-600 dark:text-amber-400 text-sm mb-3 text-center">
              Please scroll through the entire agreement before accepting
            </div>
          )}
          <FormButton
            title="I Accept"
            onClick={onAccept}
            isLoading={isLoading}
            loadingText="Processing…"
            disabled={!hasScrolledToBottom}
          />
        </div>
      </div>
    </DefaultModal>
  )
}

export default PortfolioManagementAgreementModal
